import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Paper, Tooltip } from "@mui/material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import usePermissions from "../../hooks/usePermissions";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import AdsAPIs from "../../apiRoutes/Ads";
import AgencyAPIs from "../../apiRoutes/Agency";
import {
  GET_AD_MODERATIONS_QUERY_KEY,
  GET_AGENCIES_QUERY_KEY,
} from "../../utils/constants/queryKeys";
import SectionHeader from "../common/SectionHeader";
import { ACCOUNT_OPTIONS } from "../../utils/constants/AdRequest";
import useGlobalAppAndLanguage from "../../hooks/useGlobalAppAndLanguage";
import RequestedByFilter from "../common/RequestedByFilter";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import DateRangeFilter from "../common/DateRangeFilter";
import { Download, Loader2 } from "lucide-react";
import {
  fetchAllFilteredRows,
  downloadExcelFromRows,
  exportFilename,
} from "../../utils/exportFilteredListToExcel";
import toast from "react-hot-toast";
import useZustandStore from "../../zustandStore/useZustandStore";
import {
  agencyDisplayName,
  getAgencyIdFromUser,
  isAgencyScopedUser,
} from "../../utils/agencyScope";
import { moderationTableChipStatus } from "../../utils/deliveryModeration";
import ModerationTable from "./ModerationTable";
import ModerationContextModal from "./ModerationContextModal";
import { useIssueLookup } from "./ModerationIssues";
import VideoPreviewPopup from "./VideoPreviewPopup";

const LIMIT = 20;

const AdModerationSection = () => {
  const { globalApp } = useGlobalAppAndLanguage();
  const lookupIssue = useIssueLookup();
  const issueLabel = (code) => {
    const meta = lookupIssue(code);
    return meta.bucketLabel ? `${meta.bucketLabel}:${meta.label}` : meta.label;
  };
  // Filters are seeded from the URL query so they persist across reload and are
  // shareable. `query` doubles as the deep-link param (dashboard Ad Journey table).
  const [searchParams, setSearchParams] = useSearchParams();
  const [accountFilter, setAccountFilter] = useState(() => searchParams.get("account") || "");
  const isSeekho = (app) => (app || "").toLowerCase() === "seekho";
  const [agencyFilter, setAgencyFilter] = useState(
    () => searchParams.get("agency") || ""
  );
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get("status") || ""
  );
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get("query") || ""
  );
  const [dateFrom, setDateFrom] = useState(() => {
    const v = searchParams.get("date_from");
    return v ? parseISO(v) : null;
  });
  const [dateTo, setDateTo] = useState(() => {
    const v = searchParams.get("date_to");
    return v ? parseISO(v) : null;
  });
  const [iterationMin, setIterationMin] = useState(
    () => searchParams.get("iteration_min") || ""
  );
  const [requestedByFilter, setRequestedByFilter] = useState(
    () => searchParams.get("requested_by") || ""
  );
  const [exportingExcel, setExportingExcel] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(LIMIT);
  const [contextModalAdId, setContextModalAdId] = useState(null);
  const [contextModalAdModerateId, setContextModalAdModerateId] =
    useState(null);
  const [videoPopupOpen, setVideoPopupOpen] = useState(false);
  const [videoPopupUrl, setVideoPopupUrl] = useState("");
  const axiosPrivate = useAxiosPrivate();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const scopedAgencyId = getAgencyIdFromUser(currentUser);
  const agencyScoped = isAgencyScopedUser(currentUser);
  const { hasPermission } = usePermissions();
  const canViewModeration = hasPermission(PERMISSION_CODES.AD_MODERATION_VIEW);
  const canReviewModeration = hasPermission(
    PERMISSION_CODES.AD_MODERATION_REVIEW
  );
  const canEnterErrors = hasPermission(
    PERMISSION_CODES.AD_MODERATION_ENTER_ERRORS
  );
  const canOpenModerationDetail = canViewModeration || canReviewModeration;

  const openContextModal = (adId, adModerateId) => {
    setContextModalAdId(adId);
    setContextModalAdModerateId(adModerateId);
  };
  const closeContextModal = () => {
    setContextModalAdId(null);
    setContextModalAdModerateId(null);
  };

  const openVideoPopup = (url) => {
    setVideoPopupUrl((url || "").trim());
    setVideoPopupOpen(true);
  };
  const closeVideoPopup = () => {
    setVideoPopupOpen(false);
    setVideoPopupUrl("");
  };

  // Moderators need the agency filter on the moderation page; platform-wide view only.
  const canSeeAgencyFilter = !agencyScoped && canReviewModeration;
  const { data: agenciesData } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () =>
      AgencyAPIs.list({
        axiosPrivate,
        status: "approved",
        limit: 200,
        offset: 0,
      }),
    enabled: canSeeAgencyFilter,
  });
  const agencies = agenciesData?.data ?? [];

  // Debounced search: only fire API once 400ms has passed AND at least 2 chars.
  const debouncedSearch = useDebouncedValue(searchQuery, 400);
  const effectiveSearch =
    debouncedSearch.trim().length >= 2 ? debouncedSearch.trim() : "";

  // Mirror the active filters back into the URL (replace, so no history spam).
  // The debounced search is written (as `query`), not each keystroke.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const setOrDelete = (key, val) =>
          val ? next.set(key, val) : next.delete(key);
        setOrDelete("account", accountFilter);
        if (!agencyScoped) setOrDelete("agency", agencyFilter);
        setOrDelete("status", statusFilter);
        setOrDelete("query", debouncedSearch.trim());
        setOrDelete(
          "date_from",
          dateFrom ? format(dateFrom, "yyyy-MM-dd") : ""
        );
        setOrDelete("date_to", dateTo ? format(dateTo, "yyyy-MM-dd") : "");
        setOrDelete("iteration_min", iterationMin);
        setOrDelete("requested_by", requestedByFilter);
        return next;
      },
      { replace: true }
    );
  }, [accountFilter, agencyFilter, statusFilter, debouncedSearch, dateFrom, dateTo, iterationMin, requestedByFilter, agencyScoped, setSearchParams]);

  const buildListParams = (limit, offset, opts = {}) => ({
    axiosPrivate,
    account: isSeekho(globalApp) && accountFilter ? accountFilter : undefined,
    agency_id: agencyScoped && scopedAgencyId != null ? scopedAgencyId : agencyFilter ? Number(agencyFilter) : undefined,
    status: statusFilter || undefined,
    search: effectiveSearch || undefined,
    delivery_date_from: dateFrom ? format(dateFrom, "yyyy-MM-dd") : undefined,
    delivery_date_to: dateTo ? format(dateTo, "yyyy-MM-dd") : undefined,
    iteration_gte:
      iterationMin !== "" && !Number.isNaN(Number(iterationMin))
        ? Number(iterationMin)
        : undefined,
    requested_by_id: requestedByFilter ? Number(requestedByFilter) : undefined,
    include_counts: opts.include_counts ? true : undefined,
    limit,
    offset,
  });

  const { data: result, isLoading } = useQuery({
    queryKey: [...GET_AD_MODERATIONS_QUERY_KEY, globalApp, accountFilter, agencyFilter, agencyScoped, scopedAgencyId, statusFilter, effectiveSearch, dateFrom, dateTo, iterationMin, requestedByFilter, page, pageSize],
    queryFn: () => AdsAPIs.listModerations(buildListParams(pageSize, page * pageSize)),
    placeholderData: keepPreviousData,
  });

  // Total (unconditional) counts — independent of filters/pagination, invalidated on mutations.
  const { data: totalsResult } = useQuery({
    queryKey: [
      ...GET_AD_MODERATIONS_QUERY_KEY,
      "totals",
      agencyScoped,
      scopedAgencyId,
      agencyFilter,
    ],
    queryFn: () =>
      AdsAPIs.listModerations({
        axiosPrivate,
        agency_id:
          agencyScoped && scopedAgencyId != null
            ? scopedAgencyId
            : agencyFilter
              ? Number(agencyFilter)
              : undefined,
        include_counts: true,
        limit: 1,
        offset: 0,
      }),
    staleTime: 30_000,
  });
  const totals = totalsResult?.counts ?? {
    pending: 0,
    approved: 0,
    rejected: 0,
  };

  const statusPills = [
    {
      key: "pending",
      label: "Pending",
      count: totals.pending,
      text: "text-yellow-700",
      active: "border-yellow-700 bg-yellow-50",
    },
    {
      key: "approved",
      label: "Approved",
      count: totals.approved,
      text: "text-green-700",
      active: "border-green-700 bg-green-50",
    },
    {
      key: "rejected",
      label: "Rejected",
      count: totals.rejected,
      text: "text-red-700",
      active: "border-red-700 bg-red-50",
    },
  ];

  const moderations = result?.data ?? [];
  const totalCount = result?.count ?? 0;

  const handleExportExcel = async () => {
    if (exportingExcel) return;
    setExportingExcel(true);
    toast.loading("Preparing export…", { id: "export-excel-mod" });
    try {
      const { rows, total } = await fetchAllFilteredRows({
        fetchPage: ({ limit, offset }) =>
          AdsAPIs.listModerations(buildListParams(limit, offset)),
      });
      if (!rows.length) {
        toast.dismiss("export-excel-mod");
        toast.error("No rows to export.");
        return;
      }
      const showAgencyColumn = !agencyScoped;
      await downloadExcelFromRows({
        filename: exportFilename("ad-moderations"),
        sheetName: "Moderations",
        columns: [
          { header: "Moderation ID", value: (m) => m.id ?? "" },
          {
            header: "Parent Ad ID",
            value: (m) => (m.ad_id != null && m.ad_id !== -7 ? m.ad_id : ""),
          },
          { header: "Ad deliver ID", value: (m) => m.ad_deliver_id ?? "" },
          { header: "Request ID", value: (m) => m.ad_request_id ?? "" },
          { header: "Hook number", value: (m) => m.hook_number ?? "" },
          { header: "Hook name", value: (m) => m.hook_name ?? "" },
          { header: "Ad name", value: (m) => m.ad_name ?? "" },
          { header: "Topic", value: (m) => m.topic ?? "" },
          { header: "Adset name", value: (m) => m.adset_name ?? "" },
          { header: "Module", value: (m) => m.module ?? "" },
          { header: "Category", value: (m) => m.category ?? "" },
          { header: "Track", value: (m) => m.track ?? "" },
          { header: "For app", value: (m) => m.app_name ?? "" },
          { header: "Account", value: (m) => m.account ?? "" },
          { header: "Ad type", value: (m) => m.ad_type ?? "" },
          { header: "Asset URL", value: (m) => m.asset_url ?? "" },
          { header: "Iteration", value: (m) => m.iteration ?? "" },
          { header: "Status", value: (m) => moderationTableChipStatus(m) },
          {
            header: "Finalized status",
            value: (m) => m.finalized_status ?? "",
          },
          { header: "Finalized at", value: (m) => m.finalized_at ?? "" },
          { header: "Delivery date", value: (m) => m.delivery_date ?? "" },
          { header: "Created on", value: (m) => m.created_on ?? "" },
          { header: "Updated on", value: (m) => m.updated_on ?? "" },
          {
            header: "Last moderated by",
            value: (m) => m.last_moderated_by_name ?? "",
          },
          ...(showAgencyColumn
            ? [
                {
                  header: "Delivered by agency",
                  value: (m) => m.delivered_by_agency_name ?? "",
                },
              ]
            : []),
          { header: "Launch status", value: (m) => m.launch_status ?? "" },
          { header: "Launched at", value: (m) => m.launched_at ?? "" },
          { header: "Meta Ad ID", value: (m) => m.meta_ad_id ?? "" },
          { header: "Notes", value: (m) => m.notes ?? "" },
          { header: "Errors", value: (m) => m.errors ?? "" },
          {
            header: "Issues",
            value: (m) =>
              Array.isArray(m.issues_highlighted)
                ? m.issues_highlighted.map(issueLabel).join(", ")
                : (m.issues_highlighted ?? ""),
          },
        ],
        rows,
      });
      toast.dismiss("export-excel-mod");
      toast.success(`Exported ${total} row(s).`);
    } catch (e) {
      toast.dismiss("export-excel-mod");
      if (e?.message === "EXPORT_TOO_LARGE") {
        toast.error(
          `Too many rows (${e.total}). Max export is ${e.maxRows}. Narrow filters.`
        );
      } else {
        toast.error(e?.message || "Export failed.");
      }
    } finally {
      setExportingExcel(false);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [accountFilter, agencyFilter, statusFilter, effectiveSearch, dateFrom, dateTo, iterationMin, requestedByFilter]);

  return (
    <section className="p-4 md:p-6">
      <SectionHeader title="Ad Moderation" className="mb-4" />

      <div className="flex flex-wrap items-center gap-4 mb-4">
        {isSeekho(globalApp) && (
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={accountFilter || "all"}
              onValueChange={(v) => setAccountFilter(v === "all" ? "" : v)}
            >
              <SelectTrigger className="w-[160px] bg-white border-gray-300">
                <SelectValue placeholder="All accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All accounts</SelectItem>
                {ACCOUNT_OPTIONS.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="text-sm text-gray-500 mb-3 flex flex-wrap items-center">
        {statusPills.map((item, idx) => {
          const active = statusFilter === item.key;
          return (
            <React.Fragment key={item.key}>
              {idx > 0 && <span className="mx-2">·</span>}
              <button
                type="button"
                aria-pressed={active}
                onClick={() => {
                  setStatusFilter(active ? "" : item.key);
                  setPage(0);
                }}
                className={`font-medium rounded-full px-2 py-0.5 border transition-colors hover:bg-gray-50 ${item.text} ${active ? item.active : "border-transparent"}`}
              >
                {item?.count} {item.label}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <div className="p-4 flex flex-wrap gap-3 items-center border-b border-gray-200">
          <div className="flex flex-wrap items-center gap-3 flex-1">
            <Input
              placeholder="Search…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-[180px] h-9 bg-white border-gray-300"
            />
            {canSeeAgencyFilter && (
              <Select
                value={agencyFilter || "all"}
                onValueChange={(v) => setAgencyFilter(v === "all" ? "" : v)}
              >
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All agencies</SelectItem>
                  {agencies.map((a, i) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {agencyDisplayName(a, i)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <DateRangeFilter
              label="Delivered Date"
              from={dateFrom}
              to={dateTo}
              onChange={(f, t) => {
                setDateFrom(f);
                setDateTo(t);
              }}
            />
            <Input
              type="number"
              min={1}
              placeholder="Iteration"
              value={iterationMin}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") {
                  setIterationMin("");
                  return;
                }
                const n = Number(v);
                if (Number.isNaN(n) || n < 1) return;
                setIterationMin(String(n));
              }}
              className="w-[100px] h-9 bg-white border-gray-300"
            />
            <RequestedByFilter
              value={requestedByFilter}
              onChange={setRequestedByFilter}
              agencyId={
                agencyScoped && scopedAgencyId != null
                  ? scopedAgencyId
                  : agencyFilter
                    ? Number(agencyFilter)
                    : undefined
              }
            />
            {(searchQuery ||
              agencyFilter ||
              statusFilter ||
              dateFrom ||
              dateTo ||
              iterationMin ||
              requestedByFilter) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 text-gray-500 hover:text-gray-700"
                onClick={() => {
                  setSearchQuery("");
                  setAgencyFilter("");
                  setStatusFilter("");
                  setDateFrom(null);
                  setDateTo(null);
                  setIterationMin("");
                  setRequestedByFilter("");
                  setPage(0);
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
          {canViewModeration && (
            <Tooltip title="Download Excel">
              <span className="ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 w-9 p-0 shrink-0"
                  aria-label="Download Excel"
                  disabled={exportingExcel || isLoading}
                  onClick={handleExportExcel}
                >
                  {exportingExcel ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </Button>
              </span>
            </Tooltip>
          )}
        </div>

        <ModerationTable
          moderations={moderations}
          isLoading={isLoading}
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(0);
          }}
          canOpenModerationDetail={canOpenModerationDetail}
          onOpenContext={openContextModal}
          onPreview={openVideoPopup}
        />
      </Paper>

      {videoPopupOpen && (
        <VideoPreviewPopup url={videoPopupUrl} onClose={closeVideoPopup} />
      )}

      {!!contextModalAdId && contextModalAdId !== -7 && (
        <ModerationContextModal
          adId={contextModalAdId}
          adModerateId={contextModalAdModerateId}
          onClose={closeContextModal}
          canReviewModeration={canReviewModeration}
          canEnterErrors={canEnterErrors}
        />
      )}
    </section>
  );
};

export default AdModerationSection;
